import { Tag, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Lead } from '@entities/crm';
import { UserCell } from '@entities/user-directory';

const { Text } = Typography;

interface LeadCardProps {
  lead: Lead;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onClick: () => void;
}

const SOURCE_COLOR = {
  RFQ_TARGET: 'blue',
  QUOTE: 'processing',
  ORDER: 'success',
  MANUAL: 'default',
} as const;

export function LeadCard({ lead, dragging, onDragStart, onDragEnd, onClick }: LeadCardProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  return (
    <article
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', lead.id);
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onClick}
      style={{
        cursor: 'grab',
        background: token.colorBgContainer,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 10,
        padding: 12,
        boxShadow: dragging ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
        opacity: dragging ? 0.5 : 1,
        transition: 'all 200ms ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
          {lead.title}
        </span>
        <Tag color={SOURCE_COLOR[lead.source]} style={{ marginInlineEnd: 0, fontSize: 10 }}>
          {t(`crm.source.${lead.source}`)}
        </Tag>
      </div>
      {lead.buyerId ? <UserCell userId={lead.buyerId} size="xs" /> : null}
      {lead.notes ? (
        <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
          {lead.notes}
        </Text>
      ) : null}
      <Text type="secondary" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {t('crm.card.updated', { date: new Date(lead.updatedAt).toLocaleDateString() })}
      </Text>
    </article>
  );
}
